import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { Locale } from "@repo/fika-shared/src/locale"
import { type Accessor, createContext, type ParentProps } from "solid-js"
import { type FragmentOf, graphql } from "@/graphql"

const PostVersionFragment = graphql(`
  fragment PostVersionFragment on PostVersion @_unmask {
    id
    title
    description
    content
    imageHeaderUrl
    socialImageUrl
    locale
    metaTitle
    metaDescription
    published
    sent
    translated
    updatedAt
    socialImageUrls {
      portrait
      square
      landscape
      story
    }
  }
`)

export const PostEditorFragment = graphql(
  `fragment PostEditorFragment on Post {
      id
      mainLocale
      availableLocales
      canonicalUrl
      pinnedAt
      private
      commentsEnabled
      formId
      tiers {
        id
        name
      }
      publication {
        id
        locale
        additionalLanguages
        enableMonetization
        subscriptionTiers {
          id
          name
        }
        leadMagnetForms {
          id
          name
          content
        }
      }
      suggestedTags
      tags {
        id
        tags
      }
      archivedAt
      publishedAt
      firstPublishedAt
      sentAt
      humanContent
      previewUrl: url(preview: true)
      currentVersions {
        ...PostVersionFragment
      }
      publishedVersions {
        ...PostVersionFragment
      }
  }`,
  [PostVersionFragment],
)

type PostEditorContextValue = {
  post: Accessor<FragmentOf<typeof PostEditorFragment> | undefined>
  editor: Accessor<Editor | undefined>
  activeLocale: Accessor<Locale | null>
  isLoading: Accessor<boolean>
  isStale: Accessor<boolean>
  editingDisabled: Accessor<boolean>
  isDirty: Accessor<boolean>
  isSaving: Accessor<boolean>
  isAutoSaving: Accessor<boolean>
  switchToLocale: (locale: Locale) => Promise<void>
}

export const PostEditorContext = createContext<PostEditorContextValue>()

export function PostEditorProvider(
  props: ParentProps & { value: PostEditorContextValue },
) {
  return (
    <PostEditorContext.Provider value={props.value}>
      {props.children}
    </PostEditorContext.Provider>
  )
}
